import { useState } from "react";
import { Check, Flag, MessageSquareWarning, PackageCheck } from "lucide-react";
import ProductVisual from "@/components/ProductVisual";
import StatusBadge from "@/components/StatusBadge";
import { formatRupees, QUESTIONS } from "@/lib/returns";
import { useReturns } from "@/contexts/ReturnsContext";

const reviewOrder = { Flagged: 0, Unreviewed: 1, Agreed: 2 };

function formattedDate(value) {
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

export default function OwnerReviewPage() {
  const { returns, updateReview } = useReturns();
  const [flaggingId, setFlaggingId] = useState("");
  const [note, setNote] = useState("");
  const reviewItems = returns
    .filter((item) => item.inspection && item.inspection.disposition !== "Restock")
    .sort((first, second) => {
      const statusDifference = reviewOrder[first.inspection.review_status] -
        reviewOrder[second.inspection.review_status];
      return statusDifference || new Date(second.inspection.submitted_at) - new Date(first.inspection.submitted_at);
    });
  const inspected = returns.filter((item) => item.inspection).length;
  const awaiting = reviewItems.filter((item) => item.inspection.review_status === "Unreviewed").length;
  const flagged = reviewItems.filter((item) => item.inspection.review_status === "Flagged").length;
  const markedValue = reviewItems
    .filter((item) => ["Donate", "Dispose"].includes(item.inspection.disposition))
    .reduce((sum, item) => sum + item.mrp, 0);

  const agree = (orderId) => updateReview(orderId, "Agreed");
  const submitFlag = (orderId) => {
    if (!note.trim()) {
      return;
    }
    updateReview(orderId, "Flagged", note.trim());
    setFlaggingId("");
    setNote("");
  };

  return (
    <main className="page-shell owner-page" data-testid="owner-review-page">
      <section className="page-intro owner-intro">
        <div>
          <p className="eyebrow">Owner review</p>
          <h1>Check every non-restock decision.</h1>
          <p className="intro-copy">
            Review the evidence, agree with the inspection, or flag it with a clear note.
          </p>
        </div>
      </section>
      <section className="metrics-grid" aria-label="Owner review metrics">
        <Metric label="Total returns inspected" value={inspected} testId="total-inspected-metric" />
        <Metric label="Awaiting review" value={awaiting} testId="awaiting-review-metric" />
        <Metric label="MRP marked Donate or Dispose" value={formatRupees(markedValue)} testId="marked-value-metric" />
        <Metric label="Number flagged" value={flagged} testId="flagged-count-metric" />
      </section>
      <section className="review-list" aria-label="Non-restock inspections">
        {reviewItems.map((item) => {
          const inspection = item.inspection;
          const failed = QUESTIONS.filter((question) => inspection.answers[question.id]?.value !== "good");
          const evidence = Object.values(inspection.evidence || {})[0];
          const isFlagging = flaggingId === item.order_id;
          return (
            <article className="review-card" key={item.order_id} data-testid={`review-card-${item.order_id}`}>
              <div className="review-card-media">
                <ProductVisual item={item} size="normal" />
                <ProductVisual item={item} evidence={evidence} size="normal" />
              </div>
              <div className="review-card-body">
                <div className="review-card-topline">
                  <span className="cell-code">{item.order_id}</span>
                  <span className={`review-status review-${inspection.review_status.toLowerCase()}`}>
                    {inspection.review_status}
                  </span>
                </div>
                <h2>{item.product_name}</h2>
                <p className="review-variant">{item.variant} · {formatRupees(item.mrp)}</p>
                <div className="grade-line"><strong>{inspection.grade}</strong><StatusBadge disposition={inspection.disposition} /></div>
                <div className="failed-checks">
                  <span>Failed checks</span>
                  <ul>
                    {failed.map((question) => (
                      <li key={question.id}>{question.hindi}</li>
                    ))}
                  </ul>
                </div>
                <p className="review-date">Inspected {formattedDate(inspection.submitted_at)}</p>
                {inspection.flag_note && (
                  <p className="flag-note"><MessageSquareWarning size={16} aria-hidden="true" /> {inspection.flag_note}</p>
                )}
                {isFlagging ? (
                  <div className="flag-form" data-testid={`flag-form-${item.order_id}`}>
                    <label htmlFor={`flag-note-${item.order_id}`}>Why should this be reviewed again?</label>
                    <textarea
                      id={`flag-note-${item.order_id}`}
                      value={note}
                      maxLength={240}
                      onChange={(event) => setNote(event.target.value)}
                      placeholder="Short review note"
                      data-testid={`flag-note-input-${item.order_id}`}
                    />
                    <div className="flag-actions">
                      <button
                        type="button"
                        className="button button-primary"
                        disabled={!note.trim()}
                        onClick={() => submitFlag(item.order_id)}
                        data-testid={`submit-flag-${item.order_id}-button`}
                      >
                        Save flag
                      </button>
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => setFlaggingId("")}
                        data-testid={`cancel-flag-${item.order_id}-button`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="review-actions">
                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={() => agree(item.order_id)}
                      data-testid={`agree-${item.order_id}-button`}
                    >
                      <Check size={17} aria-hidden="true" /> Agree
                    </button>
                    <button
                      type="button"
                      className="button button-flag"
                      onClick={() => { setFlaggingId(item.order_id); setNote(""); }}
                      data-testid={`flag-${item.order_id}-button`}
                    >
                      <Flag size={16} aria-hidden="true" /> Flag
                    </button>
                  </div>
                )}
              </div>
            </article>
          );
        })}
        {!reviewItems.length && (
          <div className="empty-review" data-testid="empty-review-queue">
            <PackageCheck size={30} aria-hidden="true" />
            <h2>No non-restock decisions yet.</h2>
            <p>Inspections marked Donate, Rework, or Dispose appear here.</p>
          </div>
        )}
      </section>
    </main>
  );
}

function Metric({ label, value, testId }) {
  return (
    <div className="metric-card" data-testid={testId}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}